import { useMemo } from 'react'
import { cloneDeep } from 'lodash'
import { SerializedError } from '@reduxjs/toolkit'
import { useAppSelector } from 'app/hooks'
import { useInsightContentData } from 'hooks/useInsightModalState'
import { Status } from 'features/types'
import { FetchErrorResponse } from 'api'
import { selectFluctuationPrerequisite } from 'features/insightContent/selectors'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import ServiceType from 'enums/ServiceType'
import { ServiceResultWith } from 'schemas/ServiceResultSchema'
import { SentenceRetrievalResult } from 'schemas/SentenceRetrievalResultSchema'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { TermsAndSoundBiteResult } from '../types'

const useTermsAndSoundBiteData = () => {
  const { insightSets, totalMetricValue } = useAppSelector(selectFluctuationPrerequisite)
  const insightContent = useInsightContentData<{
    status: Status
    data: TermsAndSoundBiteResult[]
    error: FetchErrorResponse | SerializedError | null
  }>(InsightContentType.FLUCTUATION_POPULAR_TERMS)

  const isLoading = !insightContent || insightContent?.status === 'loading'
  const isEmptyResult = insightContent?.status === 'succeeded' && insightContent.data.length === 0
  const isSucceeded = insightContent?.status === 'succeeded' && !isEmptyResult

  const metricValueData: ServiceResultWith<AnnotatedMetricResult>[] = insightContent?.data?.filter(
    (data) => data?.results?.[0]?.service?.type === ServiceType.METRIC_VALUE
  )
  const sentenceRetrievalData: ServiceResultWith<SentenceRetrievalResult>[] = insightContent?.data?.filter(
    (data) => data?.results?.[0]?.service?.type === ServiceType.SENTENCE_RETRIEVAL
  )

  // For each term, select a soundbite that is not deleted and has not been shown under another term,
  // to use as its example soundbite.
  const uniqueFingerprints = useMemo(() => {
    if (!sentenceRetrievalData) return []

    return sentenceRetrievalData.reduce<(string | undefined)[]>(
      (fingerprints, currentData, index) => {
        const soundBiteData = currentData?.results?.[0]?.data
        if (!soundBiteData) return fingerprints

        for (let i = 0; i < soundBiteData.sentences!.length; i += 1) {
          const document = soundBiteData.documents![soundBiteData.sentences![i]!.documentIdx!]
          if (!document.deleted) {
            const currentFingerprint = document.properties.fingerprint![0].value!
            if (
              !fingerprints.includes(currentFingerprint) ||
              i === soundBiteData.sentences!.length - 1
            ) {
              // eslint-disable-next-line no-param-reassign
              fingerprints[index] = currentFingerprint
              break
            }
          }
        }

        return fingerprints
      },
      new Array(sentenceRetrievalData.length).fill(undefined)
    )
  }, [sentenceRetrievalData])

  const sampleSoundBiteData = useMemo(
    () =>
      sentenceRetrievalData?.map((data, index) => {
        const fingerprint = uniqueFingerprints[index]
        if (fingerprint === undefined) return undefined

        const soundBiteData = cloneDeep(data.results?.[0]?.data)
        const documentIdx = soundBiteData.documents!.findIndex(
          (document) => document.properties.fingerprint?.[0].value === fingerprint
        )
        return {
          ...soundBiteData,
          sentences: soundBiteData
            .sentences!.filter((sentence) => sentence.documentIdx === documentIdx)
            .slice(0, 1)
        }
      }),
    [uniqueFingerprints, sentenceRetrievalData]
  )

  return {
    insightSets,
    totalMetricValue,
    sampleSoundBiteData,
    metricValueData,
    isLoading,
    isEmptyResult,
    isSucceeded
  }
}

export default useTermsAndSoundBiteData
